import { useState, type FormEvent } from 'react';
import { CreationLayout } from './CreationLayout';
import { NAME_SUGGESTIONS, isReservedCharacterName } from '@/data/characterNames';
import { validateCharacterName } from '@/lib/validation';

interface NamingStageProps {
  initialName: string;
  onBack: () => void;
  onContinue: (name: string) => void;
}

export function NamingStage({ initialName, onBack, onContinue }: NamingStageProps): JSX.Element {
  const [name, setName] = useState(initialName);
  const [touched, setTouched] = useState(initialName.length > 0);

  const trimmed = name.trim();
  const shapeError = validateCharacterName(name);
  const reservedError =
    !shapeError && isReservedCharacterName(trimmed) ? 'That name is spoken for.' : null;
  const error = touched ? (shapeError ?? reservedError) : null;
  const isValid = !shapeError && !reservedError && trimmed.length > 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setTouched(true);
    if (isValid) onContinue(trimmed);
  }

  function pickSuggestion(suggestion: string): void {
    setName(suggestion);
    setTouched(true);
  }

  return (
    <CreationLayout stageKey="naming" onBack={onBack} backLabel="Back">
      <form onSubmit={handleSubmit} noValidate>
        <div className="px-6 sm:px-10 pt-10 pb-2 text-center">
          <p className="text-candle-dark text-[0.7rem] uppercase tracking-[0.4em] mb-3">
            The Naming
          </p>
          <h1 className="font-display text-3xl sm:text-4xl text-ink mb-5">
            What shall they call you?
          </h1>
          <div className="mx-auto h-px w-16 bg-candle/60 mb-6" />
          <p className="text-ink-mid text-base leading-relaxed text-balance">
            They&rsquo;ll want a name for the harbor logs. For the wanted posters, when it comes to
            that. For the toasts, when they remember you fondly.
          </p>
        </div>

        <div className="px-6 sm:px-10 pt-6 pb-2">
          <label htmlFor="character-name" className="block text-sm font-medium text-ink mb-1.5">
            Your pirate name
          </label>
          <input
            id="character-name"
            name="character-name"
            type="text"
            autoComplete="off"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            maxLength={30}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!touched) setTouched(true);
            }}
            onBlur={() => setTouched(true)}
            placeholder="Enter your pirate name"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'character-name-error' : 'character-name-hint'}
            // The naming input is the single primary action on this stage,
            // mounted after an explicit "Step Ashore" click - autofocus here
            // does not interrupt screen-reader flow.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className={`w-full rounded-md border bg-parchment-light px-3 py-2.5 text-ink placeholder:text-ink-light/70 focus:outline-none focus:ring-2 focus:ring-candle focus:border-candle transition-colors text-base sm:text-sm ${
              error ? 'border-blood-bright' : 'border-ink/20'
            }`}
          />
          {error ? (
            <p id="character-name-error" className="mt-1 text-xs text-blood-bright">
              {error}
            </p>
          ) : (
            <p id="character-name-hint" className="mt-1 text-xs text-ink-light">
              2-30 characters. Letters, numbers, spaces, apostrophes, hyphens, periods.
            </p>
          )}
        </div>

        <fieldset className="px-6 sm:px-10 pt-4 pb-2">
          <legend className="text-xs uppercase tracking-wider text-ink-light mb-2">
            Suggestions
          </legend>
          <div className="flex flex-wrap gap-2">
            {NAME_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => pickSuggestion(suggestion)}
                aria-pressed={trimmed === suggestion}
                className={`inline-flex items-center rounded-full border px-4 py-2 text-sm transition-colors min-h-11 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle ${
                  trimmed === suggestion
                    ? 'border-candle bg-candle/20 text-ink'
                    : 'border-ink/20 bg-parchment-light text-ink-mid hover:border-candle hover:text-ink'
                }`}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="px-6 sm:px-10 pb-8 pt-6">
          <button
            type="submit"
            disabled={!isValid}
            className="w-full inline-flex items-center justify-center rounded-md bg-sea-deep text-candle border border-candle/40 hover:bg-sea-dark hover:border-candle px-5 py-3 font-display tracking-[0.2em] uppercase text-sm transition-colors min-h-11 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-candle focus:ring-offset-2 focus:ring-offset-parchment"
          >
            Continue
          </button>
        </div>
      </form>
    </CreationLayout>
  );
}
